import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatMap, map, of, switchMap, tap } from 'rxjs';
import { EffectsBase } from '@ps/core-data-access';
import { ToastService } from '@ps/core-overlay';
import { DecipherMappingService } from '../../services/decipher-mapping.service';
import { DecipherMappingActions } from './decipher-mapping.actions';

@Injectable()
export class DecipherMappingEffects extends EffectsBase {
  private readonly _actions$ = inject(Actions);
  private readonly _service = inject(DecipherMappingService);
  private readonly _toastService = inject(ToastService);

  public readonly loadDecipherMappings$ = createEffect(() => {
    return this._actions$.pipe(
      ofType(DecipherMappingActions.load),
      switchMap(({ cmp }) =>
        this._service.loadAll(cmp).pipe(
          map(({ localeMappings, qualificationsByLocale }) =>
            DecipherMappingActions.loadSuccess({
              localeMappings,
              qualificationsByLocale,
            }),
          ),
          this.psCatchError(
            (error) =>
              of(DecipherMappingActions.loadFailure({ error: error.message })),
            {
              notify: true,
              customMessage:
                'Failed to load decipher mappings. Please try again.',
            },
          ),
        ),
      ),
    );
  });

  public readonly saveDecipherMappings$ = createEffect(() => {
    return this._actions$.pipe(
      ofType(DecipherMappingActions.save),
      concatMap(({ cmp, locale, mappings }) =>
        this._service.save(cmp, locale, mappings).pipe(
          tap(() =>
            this._toastService.success('Default Decipher Mappings Saved'),
          ),
          map(() => DecipherMappingActions.saveSuccess({ locale, mappings })),
          this.psCatchError(
            (error) =>
              of(DecipherMappingActions.saveFailure({ error: error.message })),
            {
              notify: true,
              customMessage:
                'Failed to save decipher mappings. Please try again.',
            },
          ),
        ),
      ),
    );
  });
}
